import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, Filter, ChevronDown, X,
  ChevronLeft, ChevronRight, Sparkles, FileSpreadsheet, FileText, FileDown,
} from 'lucide-react';
import { format } from 'date-fns';
import clsx from 'clsx';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../context/AuthContext';
import { getBranding, renderExcelHeader, renderPdfHeader, brandHex } from '../../utils/exportBranding';

// ── Brand constants ───────────────────────────────────────────────────────────
// Default letterhead brand. Reports are an org's documents, so callers pass the
// logged-in org's name (user.orgName); this is only the fallback when none is known.
const COMPANY  = 'Rostery';
const APP_NAME = 'Rostery';
const PRIMARY  = [79, 70, 229];   // #4F46E5
const PRIMARY_HEX  = '4F46E5';
const LIGHT_BG_HEX = 'EEF2FF';
const ALT_ROW_HEX  = 'F1F5F9';
const BORDER_HEX   = 'E2E8F0';
const TEXT_HEX     = '1E293B';
const MUTED_HEX    = '64748B';

// ── Safely extract plain text value from a cell ───────────────────────────────
function cellText(val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object') return ''; // React elements — skip
  return String(val);
}

// ── Compact id cell ───────────────────────────────────────────────────────────
// Reports carry raw UUID id columns (shift_id, staff_id, document_id …). Showing
// the full UUID is noise; render the first segment in muted monospace with the
// full id on hover. Display-only — Excel/PDF exports read the raw row value
// (row[col.key]), so the exported file still contains the complete id.
export const fmtId = (v) => v
  ? <span className="font-mono text-xs text-slate-400" title={String(v)}>{String(v).slice(0, 8)}</span>
  : <span className="text-slate-300">–</span>;

// ── Column letter helper (A, B, … Z, AA, …) ──────────────────────────────────
function colLetter(n) {
  let s = '';
  while (n > 0) {
    n--;
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26);
  }
  return s;
}

// ── Styled Excel export (exceljs) ─────────────────────────────────────────────
export async function exportExcel(data, filename, reportTitle, columns, branding = null) {
  if (!data?.length) { toast.error('No data to export'); return; }

  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = APP_NAME;
  wb.created = new Date();

  const sheetName = reportTitle.replace(/[\\/?*[\]:]/g, '-').slice(0, 31); // Excel refuses \ / ? * [ ] : in sheet names
  const ws = wb.addWorksheet(sheetName);

  const ncols = columns.length;
  const lastCol = colLetter(ncols);
  const bHex = brandHex(branding, PRIMARY_HEX);   // org brand colour (fallback: app indigo)

  // Branded letterhead (logo + org name/details/colour). Returns the column-header row.
  const headerRowNum = renderExcelHeader(wb, ws, {
    branding,
    reportTitle,
    metaText: `Generated: ${format(new Date(), 'dd MMM yyyy  h:mm a')}   |   Total records: ${data.length}`,
    lastCol,
  });

  // Column headers (tinted with the org brand colour).
  const headerRow = ws.getRow(headerRowNum);
  headerRow.height = 24;
  columns.forEach((col, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = col.label;
    cell.font  = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
    cell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + bHex } };
    cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    cell.border = {
      top:    { style: 'medium', color: { argb: 'FF' + bHex } },
      bottom: { style: 'medium', color: { argb: 'FF' + bHex } },
      left:   { style: 'thin',   color: { argb: 'FF' + bHex } },
      right:  { style: 'thin',   color: { argb: 'FF' + bHex } },
    };
  });

  // Data rows
  data.forEach((row, ri) => {
    const exRow  = ws.getRow(ri + headerRowNum + 1);
    exRow.height = 18;
    const bgArgb = ri % 2 === 0 ? 'FFFFFFFF' : 'FF' + ALT_ROW_HEX;

    columns.forEach((col, ci) => {
      const cell = exRow.getCell(ci + 1);
      const raw  = row[col.key];
      cell.value = cellText(raw);
      cell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgArgb } };
      cell.font  = { size: 9, color: { argb: 'FF' + TEXT_HEX } };
      cell.alignment = { vertical: 'middle', indent: 1 };
      cell.border = {
        bottom: { style: 'hair', color: { argb: 'FF' + BORDER_HEX } },
        right:  { style: 'hair', color: { argb: 'FF' + BORDER_HEX } },
        left:   { style: 'hair', color: { argb: 'FF' + BORDER_HEX } },
      };
    });
  });

  // Summary row
  const summaryRowNum = data.length + headerRowNum + 1;
  ws.mergeCells(`A${summaryRowNum}:${lastCol}${summaryRowNum}`);
  const summaryCell = ws.getCell(`A${summaryRowNum}`);
  summaryCell.value = `Total: ${data.length} record${data.length !== 1 ? 's' : ''}  ·  ${(branding && branding.name) || APP_NAME}`;
  summaryCell.font  = { bold: true, size: 9, color: { argb: 'FF' + bHex } };
  summaryCell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + LIGHT_BG_HEX } };
  summaryCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  ws.getRow(summaryRowNum).height = 20;

  // Auto column widths
  columns.forEach((col, i) => {
    const maxLen = Math.min(45, Math.max(
      col.label.length + 2,
      ...data.slice(0, 200).map(r => cellText(r[col.key]).length)
    ));
    ws.getColumn(i + 1).width = maxLen + 4;
  });

  // Download
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── Styled PDF export (jspdf-autotable) ──────────────────────────────────────
export function exportPDF(data, filename, reportTitle, columns, branding = null) {
  if (!data?.length) { toast.error('No data to export'); return; }

  const isLandscape = columns.length > 7;
  const doc = new jsPDF({ orientation: isLandscape ? 'landscape' : 'portrait', unit: 'mm' });
  const pw  = doc.internal.pageSize.getWidth();
  const ph  = doc.internal.pageSize.getHeight();
  const brandFill = (() => { const c = brandHex(branding, '4F46E5'); return [parseInt(c.slice(0,2),16), parseInt(c.slice(2,4),16), parseInt(c.slice(4,6),16)]; })();

  // ── Branded letterhead (logo + org name/details/colour) ────────────────────
  const startY = renderPdfHeader(doc, branding, {
    reportTitle,
    subtitle: `Generated: ${format(new Date(), 'dd MMM yyyy h:mm a')}   |   Records: ${data.length}`,
  });

  // ── Table ─────────────────────────────────────────────────────────────────
  const tableHeaders = [columns.map(c => c.label)];
  const tableBody    = data.map(row =>
    columns.map(col => cellText(row[col.key]))
  );

  autoTable(doc, {
    head: tableHeaders,
    body: tableBody,
    startY,
    margin: { left: 12, right: 12 },
    styles: {
      fontSize: 8,
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
      textColor: [30, 41, 59],
      lineColor: [226, 232, 240],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: brandFill,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8.5,
      halign: 'left',
    },
    alternateRowStyles: {
      fillColor: [241, 245, 249],
    },
    columnStyles: {},
    didDrawPage: ({ pageNumber }) => {
      const total = doc.internal.getNumberOfPages();
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text(
        `${(branding && branding.name) || APP_NAME}  ·  ${reportTitle}  ·  Page ${pageNumber} of ${total}`,
        12, ph - 6
      );
      doc.text(format(new Date(), 'dd MMM yyyy'), pw - 12, ph - 6, { align: 'right' });
    },
  });

  doc.save(`${filename}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
}

// ── Plain CSV export (kept for compatibility) ─────────────────────────────────
export function exportCSV(data, filename) {
  if (!data?.length) { toast.error('No data to export'); return; }
  const keys = Object.keys(data[0]);
  const rows = [
    keys.join(','),
    ...data.map(r => keys.map(k => {
      const v = r[k];
      if (v === null || v === undefined) return '';
      const s = String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    }).join(',')),
  ];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── Pagination hook ───────────────────────────────────────────────────────────
export function usePagination(data, defaultPageSize = 100) {
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const total      = data?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paged      = (data || []).slice((page - 1) * pageSize, page * pageSize);
  const reset      = () => setPage(1);
  return { page, setPage, pageSize, setPageSize, total, totalPages, paged, reset };
}

// ── Filter chip ───────────────────────────────────────────────────────────────
export function FilterChip({ label, value, onRemove }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-slate-200 rounded-full text-xs text-slate-700 shadow-sm">
      <span className="text-slate-400 font-medium">{label}:</span>
      <span className="font-semibold">{value}</span>
      {onRemove && (
        <button onClick={onRemove} className="text-slate-400 hover:text-red-500 ml-0.5">
          <X size={11}/>
        </button>
      )}
    </span>
  );
}

// ── Report table with pagination ──────────────────────────────────────────────
export function ReportTable({ columns, data, loading, emptyMsg = 'No Data Available', pagination, onRowClick, selectedRowKey, rowKey }) {
  const { page, setPage, pageSize, setPageSize, total, totalPages, paged } = pagination;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gradient-to-r from-slate-800 to-slate-700">
              {columns.map(col => (
                <th key={col.key}
                  className="text-left py-3 px-4 text-white font-semibold text-xs whitespace-nowrap tracking-wide">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="py-16 text-center">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"/>
                </td>
              </tr>
            ) : paged.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center text-slate-400 text-sm">
                  {emptyMsg}
                </td>
              </tr>
            ) : paged.map((row, i) => {
              const key      = rowKey ? rowKey(row) : i;
              const selected = selectedRowKey != null && key === selectedRowKey;
              return (
                <tr key={i}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={clsx(
                    'transition-colors hover:bg-primary-50/30',
                    onRowClick && 'cursor-pointer',
                    selected ? 'bg-primary-100/70' : (i % 2 === 1 ? 'bg-slate-50/60' : 'bg-white')
                  )}>
                  {columns.map(col => (
                    <td key={col.key} className="py-2.5 px-4 whitespace-nowrap text-slate-700 text-sm">
                      {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '–')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50">
          <span className="text-xs text-slate-500 font-medium">
            {total} record{total !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <select
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white"
            >
              {[10, 25, 50, 100, 250].map(n => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-30 transition-colors"
            >
              <ChevronLeft size={13}/>
            </button>
            <span className="px-2.5 py-1 bg-primary-600 text-white rounded-lg text-xs font-bold min-w-[28px] text-center">
              {page}
            </span>
            <span className="text-xs text-slate-400">of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-30 transition-colors"
            >
              <ChevronRight size={13}/>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main ReportLayout ─────────────────────────────────────────────────────────
export default function ReportLayout({
  title,
  children,
  data = [],
  filename,
  columns,          // pass the COLS array so exports know labels
  filters,
  chart,            // optional <ReportChartRow> rendered above the table
  activeChips = [],
  showFiltersPanel,
  onToggleFilters,
}) {
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const company    = user?.orgName || COMPANY;   // on-screen subtitle
  const [branding, setBranding] = useState(null); // full letterhead for exports
  React.useEffect(() => { getBranding().then(setBranding).catch(() => {}); }, []);
  const [showExport, setShowExport]           = useState(false);
  const [aiSummary, setAiSummary]             = useState(null);
  const [summaryLoading, setSummaryLoading]   = useState(false);
  const [exporting, setExporting]             = useState(false);
  const exportRef  = useRef(null);

  const summariseWithAi = async () => {
    if (!data?.length) return toast.error('No data to summarise');
    setSummaryLoading(true);
    setAiSummary(null);
    try {
      const { data: res } = await api.post('/ai/summarise-report', { reportName: title, data });
      setAiSummary(res.summary);
    } catch {
      toast.error('AI summary failed');
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleExcel = async () => {
    setShowExport(false);
    if (!columns) { toast.error('Columns not configured for export'); return; }
    setExporting(true);
    try {
      await exportExcel(data, filename, title, columns, branding);
      toast.success('Excel exported');
    } catch (e) {
      console.error(e);
      toast.error('Excel export failed');
    } finally {
      setExporting(false);
    }
  };

  const handlePDF = () => {
    setShowExport(false);
    if (!columns) { toast.error('Columns not configured for export'); return; }
    try {
      exportPDF(data, filename, title, columns, branding);
      toast.success('PDF exported');
    } catch (e) {
      console.error(e);
      toast.error('PDF export failed');
    }
  };

  const handleCSV = () => {
    setShowExport(false);
    exportCSV(data, filename);
    toast.success('CSV exported');
  };

  // Close dropdown on outside click
  React.useEffect(() => {
    if (!showExport) return;
    const handler = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setShowExport(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showExport]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-[1500px] mx-auto px-6 py-6 space-y-5">

        {/* Top bar */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/reports/hub')}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 text-sm font-medium transition-colors group"
          >
            <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform"/>
            Back to Report Hub
          </button>

          <div className="flex items-center gap-2">
            {/* AI Summary */}
            <button
              onClick={summariseWithAi}
              disabled={summaryLoading || !data?.length}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-violet-200 bg-violet-50 text-violet-700 text-sm font-medium hover:bg-violet-100 disabled:opacity-40 transition-colors"
            >
              {summaryLoading
                ? <><div className="w-3.5 h-3.5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin"/> Summarising…</>
                : <><Sparkles size={14}/> AI Summary</>}
            </button>

            {/* Export dropdown */}
            <div className="relative" ref={exportRef}>
              <button
                onClick={() => setShowExport(v => !v)}
                disabled={exporting}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold shadow-sm transition-colors disabled:opacity-50"
              >
                {exporting
                  ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"/> Exporting…</>
                  : <><Download size={14}/> Export <ChevronDown size={12}/></>}
              </button>

              {showExport && (
                <div className="absolute right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 w-52 overflow-hidden">
                  <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Export Format</p>
                  </div>

                  <button
                    onClick={handleExcel}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors group"
                  >
                    <div className="w-7 h-7 rounded-lg bg-emerald-100 group-hover:bg-emerald-200 flex items-center justify-center transition-colors">
                      <FileSpreadsheet size={14} className="text-emerald-600"/>
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">Excel (.xlsx)</div>
                      <div className="text-[10px] text-slate-400">Styled with colors</div>
                    </div>
                  </button>

                  <button
                    onClick={handlePDF}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 transition-colors group border-t border-slate-50"
                  >
                    <div className="w-7 h-7 rounded-lg bg-red-100 group-hover:bg-red-200 flex items-center justify-center transition-colors">
                      <FileText size={14} className="text-red-600"/>
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">PDF</div>
                      <div className="text-[10px] text-slate-400">Branded, printable</div>
                    </div>
                  </button>

                  <button
                    onClick={handleCSV}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors group border-t border-slate-50"
                  >
                    <div className="w-7 h-7 rounded-lg bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center transition-colors">
                      <FileDown size={14} className="text-slate-500"/>
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">CSV</div>
                      <div className="text-[10px] text-slate-400">Raw data</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Title */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          <p className="text-xs text-slate-400 mt-0.5">{company} · {APP_NAME}</p>
        </div>

        {/* AI Summary banner */}
        {aiSummary && (
          <div className="flex items-start gap-3 p-4 bg-violet-50 border border-violet-200 rounded-2xl">
            <div className="w-8 h-8 rounded-xl bg-violet-600 flex items-center justify-center shrink-0">
              <Sparkles size={14} className="text-white"/>
            </div>
            <div className="flex-1">
              <div className="text-xs font-bold text-violet-600 mb-1 uppercase tracking-wide">AI Insight</div>
              <p className="text-sm text-violet-900 leading-relaxed">{aiSummary}</p>
            </div>
            <button onClick={() => setAiSummary(null)} className="text-violet-300 hover:text-violet-600 mt-1">
              <X size={14}/>
            </button>
          </div>
        )}

        {/* Filters bar */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={onToggleFilters}
            className={clsx(
              'flex items-center gap-2 px-3 py-1.5 rounded-xl border text-sm font-medium transition-colors',
              showFiltersPanel
                ? 'bg-primary-50 border-primary-200 text-primary-700'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            )}
          >
            <Filter size={13}/> Filters & Dates
          </button>
          {activeChips.map((chip, i) => (
            <FilterChip key={i} label={chip.label} value={chip.value} onRemove={chip.onRemove}/>
          ))}
        </div>

        {/* Filters panel */}
        {showFiltersPanel && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            {filters}
          </div>
        )}

        {/* Visual summary (charts) — only when there's data to chart */}
        {chart && data?.length > 0 && chart}

        {children}
      </div>
    </div>
  );
}
